import { TaskStatusBadge } from '@/components/badges/TaskStatusBadge';
import { formatDate } from '@/util/dates';
import IdWithCopy from '@/components/IdWithCopy';
import { JsonViewer } from '@/components/JsonViewer';
import { MetadataItem } from '@/components/MetadataItem';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { taskState } from '@/state/tasks.state';
import { useLoadableAtomValue } from '@/state/loadableHooks';
import { recordedEquipmentIds } from '@rock/hql';
import type { HomeId, TaskId } from '@rock/types';
import { ArrowLeft } from 'lucide-react';
import { type FC, useMemo } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

export const TaskDetailsPage: FC = () => {
    const { homeId, taskId } = useParams<{ homeId: HomeId; taskId: TaskId }>();
    const navigate = useNavigate();

    const [task, taskLoadable] = useLoadableAtomValue(
        useMemo(() => taskState({ homeId: homeId!, taskId: taskId! }), [homeId, taskId]),
    );
    const coveredEquipmentIds = task && recordedEquipmentIds(task);

    return (
        <div className="flex flex-col gap-6">
            <div className="flex items-start gap-3">
                <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => navigate(-1)}
                    aria-label="Back to tasks"
                    className="mt-1"
                >
                    <ArrowLeft className="h-5 w-5" />
                </Button>
                <div>
                    <h2 className="text-2xl font-semibold">Task Details</h2>
                    <IdWithCopy id={taskId ?? ''} variant="muted" className="text-sm" showLabel />
                </div>
            </div>

            {taskLoadable.state === 'loading' ? (
                <Card>
                    <CardHeader>
                        <CardTitle>Loading task...</CardTitle>
                        <CardDescription>Please wait while we fetch the task details.</CardDescription>
                    </CardHeader>
                </Card>
            ) : taskLoadable.state === 'hasError' ? (
                <Card>
                    <CardHeader>
                        <CardTitle>Error loading task</CardTitle>
                        <CardDescription>
                            {taskLoadable.error instanceof Error ? taskLoadable.error.message : `${taskLoadable.error}`}
                        </CardDescription>
                    </CardHeader>
                </Card>
            ) : !task ? (
                <Card>
                    <CardHeader>
                        <CardTitle>Task not found</CardTitle>
                        <CardDescription>We could not find this task. It may have been removed.</CardDescription>
                    </CardHeader>
                </Card>
            ) : (
                <>
                    <Card>
                        <CardHeader>
                            <CardTitle>Task Details</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="grid gap-4 md:grid-cols-2">
                                <MetadataItem label="Task ID" value={<IdWithCopy id={task.id} variant="mono" />} />
                                <MetadataItem label="Status" value={<TaskStatusBadge status={task.status} />} />
                                <MetadataItem label="Task Date" value={formatDate(task.taskDate)} />
                                <MetadataItem
                                    label="Home ID"
                                    value={
                                        <Link
                                            to={`/directory/homes/${task.homeId}`}
                                            className="font-mono text-sm text-sky-500 hover:underline"
                                        >
                                            {task.homeId}
                                        </Link>
                                    }
                                />
                                {task.subscriptionId && (
                                    <MetadataItem
                                        label="Subscription ID"
                                        value={<IdWithCopy id={task.subscriptionId} variant="mono" />}
                                    />
                                )}
                                {task.taskTypeId && (
                                    <MetadataItem
                                        label="Task Type ID"
                                        value={<IdWithCopy id={task.taskTypeId} variant="mono" />}
                                    />
                                )}
                                {coveredEquipmentIds?.length ? (
                                    <MetadataItem
                                        label="Recorded Equipment"
                                        value={
                                            <div className="flex flex-col gap-1">
                                                {coveredEquipmentIds.map((id) => (
                                                    <IdWithCopy key={id} id={id} variant="mono" />
                                                ))}
                                            </div>
                                        }
                                    />
                                ) : null}
                                {task.spaceId && (
                                    <MetadataItem
                                        label="Space ID"
                                        value={<IdWithCopy id={task.spaceId} variant="mono" />}
                                    />
                                )}
                                {task.contactId && (
                                    <MetadataItem
                                        label="Contact ID"
                                        value={<IdWithCopy id={task.contactId} variant="mono" />}
                                    />
                                )}
                                {task.completedAt && (
                                    <MetadataItem
                                        label="Completed At"
                                        value={new Date(task.completedAt).toLocaleString()}
                                    />
                                )}
                                {task.skippedAt && (
                                    <MetadataItem
                                        label="Skipped At"
                                        value={new Date(task.skippedAt).toLocaleString()}
                                    />
                                )}
                                {task.notes && <MetadataItem label="Notes" value={task.notes} />}
                                {task.taskGenerationJobRunId && (
                                    <MetadataItem
                                        label="Job Run"
                                        value={
                                            <Link
                                                to={`/system/jobs/task-generation/${task.taskGenerationJobRunId}`}
                                                className="font-mono text-sm text-sky-500 hover:underline"
                                            >
                                                {task.taskGenerationJobRunId}
                                            </Link>
                                        }
                                    />
                                )}
                                <MetadataItem label="Created At" value={new Date(task.createdAt).toLocaleString()} />
                                <MetadataItem label="Updated At" value={new Date(task.updatedAt).toLocaleString()} />
                            </div>
                        </CardContent>
                    </Card>

                    <Card>
                        <CardHeader>
                            <CardTitle>Task Object</CardTitle>
                            <CardDescription>The complete task payload stored in Firestore.</CardDescription>
                        </CardHeader>
                        <CardContent>
                            <div className="max-h-[480px] overflow-auto rounded-md bg-muted p-4">
                                <JsonViewer data={task} />
                            </div>
                        </CardContent>
                    </Card>
                </>
            )}
        </div>
    );
};
